import { jsx } from "react/jsx-runtime";
import { styled, Box, ClickAwayListener } from "@mui/material";
import { useId, useRef, useState, useCallback, useEffect } from "react";
import cn from "../../vendor/classnames/index.js";
import { spreadSx } from "../../utils/spreadSx.js";
import { inheritDataTestId } from "../../utils/dataTestId.js";
import { PinInputContainer } from "./fragments/styles.js";
import { NumberPinCell } from "./fragments/NumberPinCell.js";
import { createEvent } from "../../utils/createEvent.js";
const inRange = (hostRange, rangeToCheck) => {
  const [hostStart, hostEnd] = hostRange.start > hostRange.end ? [hostRange.end, hostRange.start] : [hostRange.start, hostRange.end], [targetStart, targetEnd] = rangeToCheck.start > rangeToCheck.end ? [rangeToCheck.end, rangeToCheck.start] : [rangeToCheck.start, rangeToCheck.end];
  return hostStart === hostEnd || targetStart === targetEnd ? !1 : hostStart === targetStart || hostEnd === targetEnd || hostStart < targetStart && hostEnd > targetStart || hostStart < targetEnd && hostEnd > targetEnd;
}, PinCodeInput = ({
  value: externalValues,
  sx,
  disabled,
  onChange,
  name,
  className,
  error,
  length = 6,
  ...props
}) => {
  const id = useId(), inputRefs = useRef({}), [internalValues, setInternalValues] = useState(
    [...Array(length)].fill("")
  ), [cellState, setCellState] = useState({}), handlePinCodeChange = useCallback((index, value) => {
    if (setInternalValues((prevState) => {
      const state = [...prevState];
      return state.splice(index, 1, value), state;
    }), value === "")
      return;
    const nextInput = inputRefs.current[index + 1];
    nextInput && nextInput.focus();
  }, []), handlePaste = useCallback(
    (data, index) => {
      var _a;
      (_a = inputRefs.current[Math.min(data.length, length - 1)]) == null || _a.focus();
      const newData = data.split("").slice(0, length), newValue = internalValues.slice(0, index).concat(newData).concat(Array(length).fill("")).slice(0, length);
      setInternalValues(newValue);
    },
    [internalValues, length]
  ), handleSelectNext = useCallback((index) => {
    var _a;
    (_a = inputRefs.current[index + 1]) == null || _a.focus();
  }, []), handleSelectPrev = useCallback(
    (index) => {
      var _a;
      Object.entries(cellState).some(([, d]) => d == null ? void 0 : d.selected) || (_a = inputRefs.current[index - 1]) == null || _a.focus();
    },
    [cellState]
  ), refs = useRef({ externalValues, internalValues, name, onChange });
  refs.current = { externalValues, internalValues, name, onChange }, useEffect(() => {
    const tmt = setTimeout(() => {
      var _a, _b, _c;
      internalValues.join("") !== ((_a = refs.current.externalValues) == null ? void 0 : _a.join("")) && ((_c = (_b = refs.current).onChange) == null || _c.call(
        _b,
        createEvent({ name: refs.current.name, value: internalValues }),
        internalValues
      ));
    }, 200);
    return () => {
      clearTimeout(tmt);
    };
  }, [internalValues]), useEffect(() => {
    externalValues != null && externalValues.join("") !== refs.current.internalValues.join("") && setInternalValues(
      [...externalValues, ...Array(length).fill("")].slice(0, length)
    );
  }, [externalValues, length]);
  const cellStateRef = useRef(cellState);
  cellStateRef.current = cellState;
  const handleKeyUp = (e) => {
    if (e.code === "KeyA" && e.ctrlKey && setCellState({ ...Array(length).fill({ selected: !0 }) }), e.key === "Backspace" && Object.values(cellState).some((v) => v == null ? void 0 : v.selected)) {
      setInternalValues((prevState) => [...prevState].map((d, i) => {
        var _a;
        return (_a = cellState[i]) != null && _a.selected ? "" : d;
      }));
      let selectionStart;
      setCellState((prev) => {
        var _a;
        const newState = Object.entries(prev).sort(([a], [b]) => Number(a) - Number(b)).reduce(
          (acc, [index, value]) => (selectionStart == null && (value != null && value.selected) && (selectionStart = Number(index)), acc[index] = { ...value, selected: !1 }, acc),
          {}
        );
        return (_a = inputRefs.current[selectionStart ?? 0]) == null || _a.focus(), newState;
      });
    }
  }, mouseDownHandler = useCallback((e) => {
    const start = { x: e.pageX, y: e.pageY };
    let end = { ...start };
    setCellState({});
    const mouseMoveHandler = (e2) => {
      end = { x: e2.pageX, y: e2.pageY }, Object.entries(inputRefs.current).forEach(([key, el]) => {
        const rect = el == null ? void 0 : el.getBoundingClientRect(), hostRange = { start: start.x, end: end.x }, targetRange = {
          start: (rect == null ? void 0 : rect.x) ?? 0,
          end: ((rect == null ? void 0 : rect.x) ?? 0) + ((rect == null ? void 0 : rect.width) ?? 0)
        };
        inRange(hostRange, targetRange) && setCellState((prev) => ({
          ...prev,
          [key]: { selected: !0 }
        }));
      });
    }, mouseUpHandler = (e2) => {
      var _a;
      end = { x: e2.pageX, y: e2.pageY };
      const [key] = Object.entries(cellStateRef.current).sort(([a], [b]) => Number(a) - Number(b)).find(([, d]) => d == null ? void 0 : d.selected) || [];
      (_a = inputRefs.current[Number(key)]) == null || _a.focus(), window.removeEventListener("mousemove", mouseMoveHandler), window.removeEventListener("mouseup", mouseUpHandler);
    };
    window.addEventListener("mousemove", mouseMoveHandler), window.addEventListener("mouseup", mouseUpHandler);
  }, []);
  return /* @__PURE__ */ jsx(
    ControlContainer,
    {
      className: cn(className, "id-control-container", "container"),
      sx: [{ marginTop: 0 }, ...spreadSx(sx)],
      disabled,
      onMouseDown: mouseDownHandler,
      "data-test-id": inheritDataTestId(props, "pin-code-container"),
      children: /* @__PURE__ */ jsx(
        ClickAwayListener,
        {
          onClickAway: () => {
            setCellState({});
          },
          children: /* @__PURE__ */ jsx(PinInputContainer, { onKeyUp: handleKeyUp, className: "pin-container", children: internalValues.map((val, i) => {
            var _a;
            return /* @__PURE__ */ jsx(
              NumberPinCell,
              {
                "data-test-id": inheritDataTestId(
                  props,
                  "number-pin-cell" + String(i)
                ),
                id: i === 0 ? id : void 0,
                index: i,
                ref: (ref) => {
                  inputRefs.current[i] = ref;
                },
                onChange: handlePinCodeChange,
                onPaste: handlePaste,
                onSelectPrev: handleSelectPrev,
                onSelectNext: handleSelectNext,
                value: val,
                error,
                selected: (_a = cellState[i]) == null ? void 0 : _a.selected,
                disabled
              },
              i
            );
          }) })
        }
      )
    }
  );
}, ControlContainer = styled(Box)(
  ({ theme: t, disabled }) => ({
    marginTop: -18,
    width: "100%",
    overflow: "hidden",
    userSelect: "none",
    ...disabled ? {
      pointerEvents: "none",
      "*": { color: t.palette.content.base.disabled },
      "input::placeholder": {
        color: `${t.palette.content.base.disabled}!important`
      }
    } : {}
  })
);
export {
  ControlContainer,
  PinCodeInput
};
//# sourceMappingURL=PinCodeInput.js.map
